const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export type PersistedCalendarVisibility = {
  calendarHandle?: string;
  visibleCalendarIds?: string[];
  defaultCalendarId?: string;
};

export type PersistedCalendarSession = {
  startField?: string;
  endField?: string;
  recurrenceField?: string;
  seriesIdField?: string;
  originalStartField?: string;
  mode?: "day" | "week" | "month";
};

const readCalendarObject = (config: unknown): Record<string, unknown> | undefined => {
  if (!isPlainObject(config)) return undefined;
  const nested = isPlainObject(config.config) ? config.config : config;
  const calendar = nested.calendar;
  return isPlainObject(calendar) ? calendar : undefined;
};

export const readPersistedCalendarVisibility = (
  config: unknown,
): PersistedCalendarVisibility => {
  const calendar = readCalendarObject(config);
  if (!calendar) return {};

  const event = isPlainObject(calendar.event) ? calendar.event : {};
  const calendarHandle =
    typeof event.calendar === "string" && event.calendar.trim()
      ? event.calendar.trim()
      : undefined;

  const visibleCalendarIds = Array.isArray(calendar.visibleCalendarIds)
    ? calendar.visibleCalendarIds.filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      )
    : undefined;
  const defaultCalendarId =
    typeof calendar.defaultCalendarId === "string" &&
    calendar.defaultCalendarId.trim()
      ? calendar.defaultCalendarId.trim()
      : undefined;

  return {
    ...(calendarHandle ? { calendarHandle } : {}),
    ...(visibleCalendarIds ? { visibleCalendarIds } : {}),
    ...(defaultCalendarId ? { defaultCalendarId } : {}),
  };
};

const readTrimmed = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

export const readPersistedCalendarSession = (
  config: unknown,
): PersistedCalendarSession => {
  const calendar = readCalendarObject(config);
  if (!calendar) return {};
  const event = isPlainObject(calendar.event) ? calendar.event : {};
  const modeRaw = calendar.mode;
  const mode =
    modeRaw === "day" || modeRaw === "week" || modeRaw === "month"
      ? modeRaw
      : undefined;
  return {
    ...(readTrimmed(event.start) ? { startField: readTrimmed(event.start) } : {}),
    ...(readTrimmed(event.end) ? { endField: readTrimmed(event.end) } : {}),
    ...(readTrimmed(event.recurrence)
      ? { recurrenceField: readTrimmed(event.recurrence) }
      : {}),
    ...(readTrimmed(event.seriesId)
      ? { seriesIdField: readTrimmed(event.seriesId) }
      : {}),
    ...(readTrimmed(event.originalStart)
      ? { originalStartField: readTrimmed(event.originalStart) }
      : {}),
    ...(mode ? { mode } : {}),
  };
};

export const findPersistedCalendarSession = (
  blocks?: Array<{ default?: boolean; config?: unknown } | null | undefined>,
): PersistedCalendarSession => {
  const list = (blocks ?? []).filter(
    (block): block is { default?: boolean; config?: unknown } => Boolean(block),
  );
  const withSession = list.find((block) => {
    const session = readPersistedCalendarSession(block.config);
    return Boolean(session.startField);
  });
  const fallback = list.find((block) => block.default) ?? list[0];
  return readPersistedCalendarSession((withSession ?? fallback)?.config);
};

export const findPersistedCalendarVisibility = (
  blocks?: Array<{ default?: boolean; config?: unknown } | null | undefined>,
): PersistedCalendarVisibility => {
  const list = (blocks ?? []).filter(
    (block): block is { default?: boolean; config?: unknown } => Boolean(block),
  );
  const withCalendar = list.find((block) => {
    const visibility = readPersistedCalendarVisibility(block.config);
    return Boolean(visibility.calendarHandle || visibility.visibleCalendarIds);
  });
  const fallback = list.find((block) => block.default) ?? list[0];
  return readPersistedCalendarVisibility((withCalendar ?? fallback)?.config);
};

export const readPersistedCalendarFieldMap = (
  config: unknown,
): Record<string, string> | undefined => {
  const calendar = readCalendarObject(config);
  if (!calendar || !isPlainObject(calendar.calendarFieldMap)) return undefined;
  const map = Object.fromEntries(
    Object.entries(calendar.calendarFieldMap).filter(
      (entry): entry is [string, string] =>
        typeof entry[0] === "string" &&
        typeof entry[1] === "string" &&
        entry[1].length > 0,
    ),
  );
  return Object.keys(map).length > 0 ? map : undefined;
};
